import type {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import {
  maxDecimalPlaces,
  maxValue,
  minValue,
  required,
  wholeNumber,
} from '@freelancer/ui/validators';
import { isDefined, isFormGroup, toNumber } from '@freelancer/utils';
import { QuotationItemUnitTypeApi } from 'api-typings/quotations/quotations';
import type { QuotationItemFormGroup } from './quotation-form-helper.types';

/**
 * Extra validators specific to Quotations.
 *
 * Subject to change based on business requirements.
 */

export const MIN_DATE_ERROR_KEY = 'MIN_DATE_ERROR';
export const MIN_TOTAL_ERROR_KEY = 'MIN_TOTAL_ERROR';
export const SUBSCRIPITON_AMOUNT_MINIMUM_ERROR_KEY =
  'SUBSCRIPITON_AMOUNT_MINIMUM_ERROR';
export const FIXED_ITEM_AMOUNT_MINIMUM_ERROR_KEY = 'FIXED_ITEM_MINIMUM_ERROR';
export const HOURLY_RATE_ERROR_KEY = 'HOURLY_RATE_ERROR';
export const WEEKLY_LIMIT_ERROR_KEY = 'WEEKLY_LIMIT_ERROR';
export const SUBSCRIPTION_AMOUNT_ERROR_KEY = 'SUBSCRIPTION_AMOUNT_ERROR';

export const AUSTRALIAN_MINIMUM_HOURLY_BID_AMOUNT = 25;
export const NON_AUSTRALIAN_MINIMUM_HOURLY_BID_AMOUNT = 2;

// Validates the custom date range formgroup to ensure that the end date is greater than the start date.
export const validateFixedProjectBidDateRange =
  ({
    allowZeroDayTimeline,
    startDateKey,
    endDateKey,
    errorText,
  }: {
    allowZeroDayTimeline: boolean;
    startDateKey: string;
    endDateKey: string;
    errorText: string;
  }): ValidatorFn =>
  (control: AbstractControl): ValidationErrors | null => {
    const startDate: Date = control.get(startDateKey)?.value;
    const endDate: Date = control.get(endDateKey)?.value;

    // Create new copy of object, so we don't increment original startDate object
    // that is being used in the form.
    const minimumDate = new Date(startDate);
    minimumDate.setHours(0, 0, 0, 0);

    if (!startDate || !endDate) {
      return null;
    }

    if (!allowZeroDayTimeline) {
      // If we don't allow users to complete quotations on the same day as they start
      // endDate has to be equal or more than one day of the starting date.
      minimumDate.setDate(minimumDate.getDate() + 1);
    }

    // Assuming that control.value is of a correct date format
    return endDate < minimumDate ? { [MIN_DATE_ERROR_KEY]: errorText } : null;
  };

// Validates that the sum of properties of a form array is greater than or equal to the minimum `min`.
// `minimumValidControls` can also be set to only invalidate so that a certain
// number of the properties must be valid before the error message appears.
export const validateMinimum =
  (min: number, computeError: (total: number) => string): ValidatorFn =>
  (control: AbstractControl): { [key: string]: any } | null => {
    const form = control as FormArray<FormGroup<QuotationItemFormGroup>>;

    const total: number = form.controls.reduce(
      (acc: number, current: FormGroup<QuotationItemFormGroup>) => {
        const amountControl = current.controls.price;
        const unitTypeControl = current.controls.unitType;
        const unitLimitControl = current.controls.unitLimit;

        const amount = amountControl?.valid ? toNumber(amountControl.value) : 0;

        if (unitTypeControl.value === QuotationItemUnitTypeApi.HOUR) {
          const unitLimit =
            unitLimitControl?.valid && isDefined(unitLimitControl.value)
              ? unitLimitControl.value
              : 0;
          return acc + (amount >= 0 && unitLimit >= 0 ? amount * unitLimit : 0);
        }

        return acc + Math.max(amount, 0);
      },
      0,
    );

    // Show only when all forms are filled out and valid.
    const everyFormIsValid = allControlsValid(form);

    return total < min && everyFormIsValid
      ? { [MIN_TOTAL_ERROR_KEY]: computeError(total) }
      : null;
  };

export const validateSubscriptionTotal =
  (min: number, computeError: (total: number) => string): ValidatorFn =>
  (control: AbstractControl): { [key: string]: any } | null => {
    const form = control as FormArray<FormGroup<QuotationItemFormGroup>>;

    const subscriptionControls = form.controls.filter(
      item => item.controls.billingFrequency !== undefined,
    );

    if (subscriptionControls.length === 0 || !allControlsValid(form)) {
      return null;
    }

    const total: number = subscriptionControls.reduce(
      (acc: number, current: FormGroup<QuotationItemFormGroup>) => {
        const amountControl = current.controls.price;

        const amount = amountControl?.valid ? toNumber(amountControl.value) : 0;

        return acc + Math.max(amount, 0);
      },
      0,
    );

    return total < min
      ? { [SUBSCRIPITON_AMOUNT_MINIMUM_ERROR_KEY]: computeError(total) }
      : null;
  };

export const validateFixedItemsTotal =
  (min: number, computeError: (total: number) => string): ValidatorFn =>
  (control: AbstractControl): { [key: string]: any } | null => {
    const form = control as FormArray<FormGroup<QuotationItemFormGroup>>;

    const fixedItemControls = form.controls.filter(
      item =>
        item.controls.billingFrequency === undefined &&
        item.controls.unitType.value !== QuotationItemUnitTypeApi.HOUR,
    );

    if (fixedItemControls.length === 0 || !allControlsValid(form)) {
      return null;
    }

    const total: number = fixedItemControls.reduce(
      (acc: number, current: FormGroup<QuotationItemFormGroup>) => {
        const amountControl = current.controls.price;

        const amount = amountControl?.valid ? toNumber(amountControl.value) : 0;

        return acc + Math.max(amount, 0);
      },
      0,
    );

    return total < min
      ? { [FIXED_ITEM_AMOUNT_MINIMUM_ERROR_KEY]: computeError(total) }
      : null;
  };

/**
 * Trims a string before checking the length.
 */
export const validateMinLengthTrimmed =
  (length: number, errorText: string): ValidatorFn =>
  (control: AbstractControl): { [key: string]: any } | null => {
    const controlValue = control.value;

    if (
      controlValue === undefined ||
      controlValue === null ||
      typeof controlValue !== 'string'
    ) {
      return null;
    }

    return controlValue.trim().length < length
      ? { MIN_LENGTH_ERROR: errorText }
      : null;
  };

export const validateHourlyRate =
  ({
    isUserInAu,
    maxHourlyRate,
    minHourlyRate,
    formatAmount,
  }: {
    isUserInAu: boolean;
    maxHourlyRate: number;
    minHourlyRate?: number;
    formatAmount(amount: number): string | null;
  }): ValidatorFn =>
  (control: AbstractControl): { [key: string]: string } | null => {
    let errorText = '';

    if (!isFormGroup(control)) {
      return {
        [WEEKLY_LIMIT_ERROR_KEY]: $localize`Hourly rate validaton failed`,
      };
    }
    const priceControl = control.controls.price;

    if (priceControl.dirty) {
      if (required(errorText)(priceControl)) {
        errorText = $localize`Enter an hourly rate.`;
      } else if (
        isUserInAu &&
        minValue(
          minHourlyRate ?? AUSTRALIAN_MINIMUM_HOURLY_BID_AMOUNT,
          errorText,
        )(priceControl)
      ) {
        errorText = $localize`Australian users must quote at least ${formatAmount(
          minHourlyRate ?? AUSTRALIAN_MINIMUM_HOURLY_BID_AMOUNT,
        )} per hour by regulation.`;
      } else if (
        !isUserInAu &&
        minValue(
          minHourlyRate ?? NON_AUSTRALIAN_MINIMUM_HOURLY_BID_AMOUNT,
          errorText,
        )(priceControl)
      ) {
        errorText = $localize`Enter a rate higher than ${formatAmount(
          minHourlyRate ?? NON_AUSTRALIAN_MINIMUM_HOURLY_BID_AMOUNT,
        )}.`;
      } else if (maxValue(maxHourlyRate, errorText)(priceControl)) {
        errorText = $localize`Rate must be below ${formatAmount(
          maxHourlyRate,
        )}.`;
      } else if (maxDecimalPlaces(2, errorText)(priceControl)) {
        errorText = $localize`Ensure hourly rate is no more than 2 decimal places.`;
      }
    }

    return errorText ? { [HOURLY_RATE_ERROR_KEY]: errorText } : null;
  };

export const validateWeeklyLimit =
  ({
    minWeeklyLimit,
    maxWeeklyLimit,
  }: {
    minWeeklyLimit: number;
    maxWeeklyLimit: number;
  }): ValidatorFn =>
  (control: AbstractControl): { [key: string]: string } | null => {
    let errorText = '';

    if (!isFormGroup(control)) {
      return {
        [WEEKLY_LIMIT_ERROR_KEY]: $localize`Weekly limit validaton failed`,
      };
    }

    const unitLimitControl = control.controls.unitLimit;
    if (unitLimitControl.dirty) {
      if (required(errorText)(unitLimitControl)) {
        errorText = $localize`Enter weekly limit.`;
      } else if (minValue(minWeeklyLimit, errorText)(unitLimitControl)) {
        errorText = $localize`Limit must be greater than 0.`;
      } else if (maxValue(maxWeeklyLimit, errorText)(unitLimitControl)) {
        errorText = $localize`Limit must be below 168.`;
      } else if (wholeNumber(errorText)(unitLimitControl)) {
        errorText = $localize`Limit must be a whole number.`;
      }
    }
    return errorText ? { [WEEKLY_LIMIT_ERROR_KEY]: errorText } : null;
  };

export const validateSubscriptionAmount =
  (): ValidatorFn =>
  (control: AbstractControl): { [key: string]: string } | null => {
    let errorText = '';

    if (!isFormGroup(control)) {
      return {
        [WEEKLY_LIMIT_ERROR_KEY]: $localize`Subscription amount validaton failed`,
      };
    }

    const amountControl = control.controls.price;

    if (amountControl.dirty) {
      if (required(errorText)(amountControl)) {
        errorText = $localize`Enter an amount.`;
      } else if (minValue(1, errorText)(amountControl)) {
        errorText = $localize`Amount must be greater than 1.`;
      } else if (maxValue(999_999_999, errorText)(amountControl)) {
        errorText = $localize`Enter a smaller amount.`;
      } else if (maxDecimalPlaces(2, errorText)(amountControl)) {
        errorText = $localize`Ensure amount has no more than 2 decimal places.`;
      }
    }
    return errorText ? { [SUBSCRIPTION_AMOUNT_ERROR_KEY]: errorText } : null;
  };

function allControlsValid(
  form: FormArray<FormGroup<QuotationItemFormGroup>>,
): boolean {
  return (
    form.controls.length >= 1 &&
    form.controls.every(current => {
      const nameControl = current.controls.name;
      const amountControl = current.controls.price;
      const unitTypeControl = current.controls.unitType;
      const unitLimitControl = current.controls.unitLimit;

      if (
        current.invalid ||
        nameControl.value === '' ||
        nameControl.invalid ||
        amountControl.value === '' ||
        amountControl.invalid
      ) {
        return false;
      }

      if (
        unitTypeControl.value === QuotationItemUnitTypeApi.HOUR &&
        (!isDefined(unitLimitControl.value) || unitLimitControl.invalid)
      ) {
        return false;
      }

      return true;
    })
  );
}
